import { Vector3 } from 'three'
import type { RoadSpline } from '../core/spline/splineGenerator'
import { convertPosition } from '../core/unity'
import { ROAD_WIDTH } from '../render/roadMesh'

/**
 * Flat collision geometry for the road, in three.js space.
 *
 * `SplineMesh` — the generated road — is the collider tagged `Conveyor` that
 * `CatchBlockerBus` looks for, so "can this bus leave the lot" is literally
 * "does a ray from its nose reach the road". The road is a swept 6.5-wide
 * strip, which flattens to a ribbon of quads on XZ.
 *
 * Built once per level: probing the spline per ray step would mean thousands of
 * `project()` calls, each a full scan of the arc-length table.
 */
export class RoadCollider {
  /** Centre-line samples, three.js space. */
  private readonly centre: { x: number; z: number }[] = []
  /** Left/right edge samples, three.js space. */
  private readonly left: { x: number; z: number }[] = []
  private readonly right: { x: number; z: number }[] = []
  /** Spline percent at each sample. */
  private readonly percent: number[] = []

  constructor(road: RoadSpline, spacing = 1.0) {
    const { spline, originalStartPercent, originalEndPercent } = road
    const from = spline.distanceAt(originalStartPercent)
    const to = spline.distanceAt(originalEndPercent)
    const steps = Math.max(2, Math.ceil((to - from) / spacing))

    const pos = new Vector3()
    const tangent = new Vector3()
    const half = ROAD_WIDTH / 2

    for (let i = 0; i <= steps; i++) {
      const percent = spline.percentAtDistance(from + ((to - from) * i) / steps)
      spline.evaluatePosition(percent, pos)
      spline.evaluateTangent(percent, tangent)
      tangent.y = 0
      if (tangent.lengthSq() < 1e-12) tangent.set(0, 0, 1)
      tangent.normalize()

      convertPosition(pos, pos)
      convertPosition(tangent, tangent)

      // Left/right of travel on XZ.
      const sx = -tangent.z
      const sz = tangent.x

      this.percent.push(percent)
      this.centre.push({ x: pos.x, z: pos.z })
      this.left.push({ x: pos.x + sx * half, z: pos.z + sz * half })
      this.right.push({ x: pos.x - sx * half, z: pos.z - sz * half })
    }
  }

  /**
   * Nearest intersection of a ray with the road surface.
   * Returns the distance along the ray and the spline percent there.
   */
  raycast(
    ox: number,
    oz: number,
    dx: number,
    dz: number,
    maxDistance = Infinity,
  ): { distance: number; percent: number } | null {
    let best: { distance: number; percent: number } | null = null

    for (let i = 0; i < this.centre.length - 1; i++) {
      // Quad edges for this ribbon segment.
      const edges: [{ x: number; z: number }, { x: number; z: number }, number][] = [
        [this.left[i], this.left[i + 1], i],
        [this.right[i], this.right[i + 1], i],
        [this.left[i], this.right[i], i],
        [this.left[i + 1], this.right[i + 1], i + 1],
      ]
      for (const [a, b, sampleIndex] of edges) {
        const t = raySegment(ox, oz, dx, dz, a.x, a.z, b.x, b.z)
        if (t === null || t > maxDistance) continue
        if (best === null || t < best.distance) {
          best = { distance: t, percent: this.percent[sampleIndex] }
        }
      }
    }
    return best
  }

  /** Is this point on the tarmac? */
  contains(x: number, z: number): boolean {
    // A point is inside if a ray from it crosses the ribbon boundary an odd
    // number of times; cheaper here to just check proximity to the centre line.
    const half = ROAD_WIDTH / 2
    for (let i = 0; i < this.centre.length - 1; i++) {
      const d = pointSegmentDistance(x, z, this.centre[i], this.centre[i + 1])
      if (d <= half) return true
    }
    return false
  }

  /** Spline percent of the centre-line sample nearest to a point. */
  nearestPercent(x: number, z: number): number {
    let best = 0
    let bestSq = Infinity
    for (let i = 0; i < this.centre.length; i++) {
      const dx = this.centre[i].x - x
      const dz = this.centre[i].z - z
      const sq = dx * dx + dz * dz
      if (sq < bestSq) {
        bestSq = sq
        best = i
      }
    }
    return this.percent[best]
  }
}

/** Ray (origin + t·dir, t ≥ 0) against segment AB. Returns t, or null. */
function raySegment(
  ox: number,
  oz: number,
  dx: number,
  dz: number,
  ax: number,
  az: number,
  bx: number,
  bz: number,
): number | null {
  const ex = bx - ax
  const ez = bz - az
  const denom = dx * ez - dz * ex
  if (Math.abs(denom) < 1e-12) return null

  const px = ax - ox
  const pz = az - oz
  const t = (px * ez - pz * ex) / denom
  const u = (px * dz - pz * dx) / denom

  if (t < 0 || u < 0 || u > 1) return null
  return t
}

function pointSegmentDistance(
  x: number,
  z: number,
  a: { x: number; z: number },
  b: { x: number; z: number },
): number {
  const ex = b.x - a.x
  const ez = b.z - a.z
  const lenSq = ex * ex + ez * ez
  if (lenSq < 1e-12) return Math.hypot(x - a.x, z - a.z)
  let t = ((x - a.x) * ex + (z - a.z) * ez) / lenSq
  t = t < 0 ? 0 : t > 1 ? 1 : t
  return Math.hypot(x - (a.x + ex * t), z - (a.z + ez * t))
}
